import {
  Day,
  addDays,
  addWeeks,
  differenceInCalendarDays,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
} from 'date-fns';

export default function getDays(from: Date, to: Date): Date[] {
  const dayCount = Math.max(0, differenceInCalendarDays(to, from) + 1);
  const days = Array.from({ length: dayCount }, (_, index) =>
    addDays(from, index)
  );

  // This is to handle when you select the same day twice
  // we want a range of that day to that same day
  if (days.length === 1) {
    days.push(days[0]);
  }

  return days;
}

/**
 * Returns all seven days of the week containing the given date.
 * @param day - The date to get the week for
 * @param weekStartsOn - The day the week starts on (0 = Sunday, 1 = Monday). Defaults to 1 (Monday).
 */
export function getDaysInWeek(day: Date, weekStartsOn: Day = 1): Date[] {
  const weekStart = startOfWeek(day, { weekStartsOn });
  return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
}

/**
 * Returns every day of the calendar-month grid containing `anchor`: the full
 * weeks (35–42 days) spanning from the start of the week of the 1st to the end
 * of the week of the last day of the month. Always a whole number of weeks so
 * it tiles a 7-column grid.
 *
 * A non-leap February that aligns exactly to the week boundaries (e.g. Feb 2021
 * with a Monday start, or Feb 2026 with a Sunday start) naturally spans only
 * four weeks; we pad it by one trailing week so the grid is always at least five
 * rows, keeping the layout consistent month to month.
 *
 * @param anchor - Any date within the target month
 * @param weekStartsOn - The day the week starts on (0 = Sunday, 1 = Monday). Defaults to 1 (Monday).
 */
export function getDaysInMonthGrid(
  anchor: Date,
  weekStartsOn: Day = 1
): Date[] {
  const gridStart = startOfWeek(startOfMonth(anchor), { weekStartsOn });
  const gridEnd = endOfWeek(endOfMonth(anchor), { weekStartsOn });
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd });
  return days.length >= 35
    ? days
    : eachDayOfInterval({ start: gridStart, end: addWeeks(gridEnd, 1) });
}

/**
 * The median day of a week — index 3 of a 7-day week. Because the median always
 * sits in whichever month owns four or more of the week's days, it identifies
 * the month a boundary-straddling week "belongs" to (the ISO convention of using
 * the week's Thursday).
 */
export function getWeekMidpoint(week: Date[]): Date {
  return week[Math.floor(week.length / 2)];
}

/**
 * The month the month view should show for a given week. Normally the month
 * that owns the majority of the week's days (via {@link getWeekMidpoint}), but
 * a week containing today anchors to today's month — even when most of that
 * week spills into the neighbouring month — since that's the month the user is
 * actually in.
 */
export function getMonthAnchorForWeek(week: Date[]): Date {
  const today = new Date();
  const weekContainsToday = week.some((day) => isSameDay(day, today));
  return startOfMonth(weekContainsToday ? today : getWeekMidpoint(week));
}

/**
 * The week to open when switching from the month view into the week view for the
 * month containing `anchor`: the first week whose majority of days fall in that
 * month. A month whose 1st lands late in the week (so the 1st's week is mostly
 * the previous month) opens on the following week instead, so the week view
 * never lands on a mostly-previous-month week.
 *
 * @param anchor - Any date within the target month
 * @param weekStartsOn - The day the week starts on (0 = Sunday, 1 = Monday). Defaults to 1 (Monday).
 */
export function getFirstWeekOfMonth(
  anchor: Date,
  weekStartsOn: Day = 1
): Date[] {
  const monthStart = startOfMonth(anchor);
  const firstWeek = getDaysInWeek(monthStart, weekStartsOn);
  return isSameMonth(getWeekMidpoint(firstWeek), monthStart)
    ? firstWeek
    : getDaysInWeek(addWeeks(monthStart, 1), weekStartsOn);
}
